/**
 * BranchUtilisationBars — horizontal bar chart for per-branch capacity
 * utilisation across the selected period.
 *
 * No chart library — CSS widths, deliberate plain-text labels on both
 * sides (branch name · %). Bars are scaled to a fixed 100% baseline so
 * "over 100%" (manual overrides / spillover) sticks out as an obvious
 * red overflow segment, mirroring the mockup's "Unit 4 Manesar 105%".
 *
 * Empty / zero-capacity branches are filtered out so the chart doesn't
 * fill with "0%" lines.
 */
export function BranchUtilisationBars({ rows = [], periodLabel = "today", testId = "branch-util-bars" }) {
  const visible = (rows || [])
    .filter((r) => (r?.capacity || 0) > 0)
    .sort((a, b) => (b.util_pct || 0) - (a.util_pct || 0));

  if (visible.length === 0) {
    return (
      <div className="border border-border bg-white p-6 text-xs font-mono text-muted-foreground italic">
        No branches with allocated capacity in this window.
      </div>
    );
  }

  const tone = (pct) => {
    if (pct > 100) return "bg-red-500";
    if (pct >= 95) return "bg-amber-500";
    if (pct >= 70) return "bg-green-600";
    if (pct >= 40) return "bg-zinc-400";
    return "bg-zinc-300";
  };
  const textTone = (pct) => {
    if (pct > 100) return "text-red-600";
    if (pct >= 95) return "text-amber-700";
    if (pct >= 70) return "text-green-700";
    return "text-zinc-700";
  };

  return (
    <div className="border border-border bg-white" data-testid={testId}>
      <div className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] font-bold">
          Branch Capacity Utilisation · <span className="text-muted-foreground">{periodLabel}</span>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {visible.length} active branches
        </div>
      </div>
      <div className="p-6 space-y-4">
        {visible.map((r) => {
          const pct = r.util_pct || 0;
          // Cap bar width visually at 100%; show overflow as a small red
          // chip on the right so >100% reads instantly.
          const widthPct = Math.min(100, pct);
          const overflow = Math.max(0, pct - 100);
          return (
            <div key={r.branch} className="grid grid-cols-[140px_1fr_56px] items-center gap-4" data-testid={`branch-bar-${r.branch}`}>
              <div className="text-xs font-mono text-zinc-800 truncate" title={r.branch}>
                {r.branch}
              </div>
              <div className="relative h-3 bg-zinc-100 border border-border overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 ${tone(pct)} transition-all`}
                  style={{ width: `${widthPct}%` }}
                />
                {overflow > 0 && (
                  <div
                    className="absolute inset-y-0 right-0 bg-red-700 border-l border-white"
                    style={{ width: `${Math.min(15, overflow / 2)}%` }}
                    title={`+${overflow.toFixed(1)}% over capacity`}
                  />
                )}
              </div>
              <div className={`text-xs font-mono font-bold text-right ${textTone(pct)}`}>
                {pct.toFixed(0)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BranchUtilisationBars;
